import { ReactNode, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import AdminSidebar from './AdminSidebar'
import BritLogo from '../assets/white-brit-logo.svg'

type Props = {
  children: ReactNode
}

export default function AdminLayout({ children }: Props) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()

  // Close drawer on route change so users don't end up stuck on a navigated page with the menu still open
  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  // Lock body scroll when the drawer is open on mobile
  useEffect(() => {
    if (!sidebarOpen) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [sidebarOpen])

  return (
    <div className='relative flex h-screen bg-[#f7f9fc] overflow-hidden'>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className='fixed inset-0 z-40 bg-black/50 lg:hidden'
          onClick={() => setSidebarOpen(false)}
          aria-hidden='true'
        />
      )}

      {/* Sidebar — fixed/drawer on mobile, static on desktop */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <AdminSidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main column */}
      <div className='flex flex-1 flex-col min-w-0'>
        {/* Mobile top bar */}
        <div className='lg:hidden flex items-center justify-between bg-[#0f0b10] px-4 py-3 text-white shadow-md flex-shrink-0'>
          <div className='flex items-center gap-2'>
            <img src={BritLogo} alt='BritFiler' className='h-8 w-auto' />
          </div>
          <button
            onClick={() => setSidebarOpen(true)}
            className='p-2 rounded-md text-white hover:bg-white/10'
            aria-label='Open menu'
          >
            <Menu className='h-6 w-6' />
          </button>
        </div>

        <main className='flex-1 overflow-y-auto'>{children}</main>
      </div>
    </div>
  )
}
